import { parseHotkeys, type KeyCombo } from './keys';

export type ShortcutGroup = 'Global' | 'Navigation' | 'Lists' | 'Editors';

export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = ['Global', 'Navigation', 'Lists', 'Editors'];

export interface ShortcutDefinition {
  /** Stable and unique; registering the same id again replaces the earlier definition. */
  id: string;
  /** `'mod+k'`, `'g s'` (a sequence), `'/'`; several alternatives as an array. */
  keys: string | readonly string[];
  /** What it does, for the `?` sheet. */
  label: string;
  group: ShortcutGroup;
  handler: (event: KeyboardEvent) => void;
  /** Only fires while this returns true (the list has rows, the editor is dirty…). */
  when?: () => boolean;
  /** Fires even while typing in an input, textarea or contenteditable. Off by default. */
  allowInInputs?: boolean;
  /** Left out of `useShortcutList()`. */
  hidden?: boolean;
}

export interface RegisteredShortcut {
  definition: ShortcutDefinition;
  /** The parsed alternatives, each a sequence of chords. */
  sequences: KeyCombo[][];
  /** Registration order; the latest registration wins a conflict. */
  order: number;
}

export interface ShortcutRegistry {
  /** Adds (or replaces, by id) and returns the matching unregister. */
  register(definition: ShortcutDefinition): () => void;
  unregister(id: string): void;
  get(id: string): RegisteredShortcut | undefined;
  /** Every registration, oldest first. The array identity only changes when the registry does. */
  list(): readonly RegisteredShortcut[];
  subscribe(callback: () => void): () => void;
}

export function createShortcutRegistry(): ShortcutRegistry {
  const entries = new Map<string, RegisteredShortcut>();
  const listeners = new Set<() => void>();
  let order = 0;
  let snapshot: readonly RegisteredShortcut[] = [];
  const changed = () => {
    snapshot = [...entries.values()].sort((a, b) => a.order - b.order);
    listeners.forEach((listener) => listener());
  };
  return {
    register: (definition) => {
      const keys = typeof definition.keys === 'string' ? [definition.keys] : definition.keys;
      const sequences = keys.map(parseHotkeys).filter((sequence) => sequence.length > 0);
      const entry: RegisteredShortcut = { definition, sequences, order: order++ };
      entries.set(definition.id, entry);
      changed();
      return () => {
        if (entries.get(definition.id) === entry) {
          entries.delete(definition.id);
          changed();
        }
      };
    },
    unregister: (id) => {
      if (entries.delete(id)) changed();
    },
    get: (id) => entries.get(id),
    list: () => snapshot,
    subscribe: (callback) => {
      listeners.add(callback);
      return () => {
        listeners.delete(callback);
      };
    },
  };
}

/** The registry a `HotkeysProvider` without its own uses; feature modules register here at startup. */
export const shortcutRegistry: ShortcutRegistry = createShortcutRegistry();

/** Registers on the default registry (or the given one) and returns the unregister. */
export function registerShortcut(definition: ShortcutDefinition, registry: ShortcutRegistry = shortcutRegistry): () => void {
  return registry.register(definition);
}
